import { Box, Tooltip, Typography } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import {
  PAY_FILE_APPROVED_WARNING_TOOLTIP,
  PAY_FILE_STATUS_TO_CHIP_CLASS_NAME,
  PAY_FILE_STATUS_TO_LABEL,
} from "../constants"
import { getPayFileFailure } from "../utils"

import StyledChip from "@/components/common/Chip/Chip"
import { type PayFileListFieldsFragment, PayFileStatus } from "@/graphql/codegen/graphql"

interface PayFileStatusCellProps {
  payFile: PayFileListFieldsFragment
}

export const PayFileStatusCell: FC<PayFileStatusCellProps> = ({ payFile }) => {
  const failure = getPayFileFailure(payFile.latestLog)

  return (
    <Box className='flex items-center gap-2'>
      <StyledChip
        label={PAY_FILE_STATUS_TO_LABEL[payFile.status]}
        className={PAY_FILE_STATUS_TO_CHIP_CLASS_NAME[payFile.status]}
        size='small'
      />
      {payFile.status === PayFileStatus.Approved && (
        // The span carries tabIndex so the tooltip explaining the problem is reachable by keyboard,
        // not just hover; MUI puts the title on it as an accessible name.
        <Tooltip
          title={
            failure ? (
              <>
                <Typography variant='caption' className='block font-bold'>
                  {failure.title}
                </Typography>
                {failure.description && (
                  <Typography variant='caption' className='block'>
                    {failure.description}
                  </Typography>
                )}
              </>
            ) : (
              PAY_FILE_APPROVED_WARNING_TOOLTIP
            )
          }
        >
          <Box
            component='span'
            tabIndex={0}
            // MUI only labels the trigger from a string title, so spell out the failure for keyboard and screen reader users.
            {...(failure && { "aria-label": [failure.title, failure.description].filter(Boolean).join(" ") })}
            className='flex'
            data-testid={`pay-file.status-cell.warning-icon-${payFile.payFileId}`}
          >
            <WarningOutlined className='text-secondary-600' fontSize='small' />
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}
